/* eslint-disable no-restricted-properties */
/* eslint-disable @typescript-eslint/ban-ts-comment */

import qs from 'qs';
import mapValues from 'lodash/mapValues';

// eslint-disable-next-line import/no-cycle
import {
  getAccessPortals,
  isAccessToSelectPortal,
  normalizeValue,
  parseArrays,
  logError,
} from 'shared/functions/utils';
import {
  agentPrefix,
  authPrefix,
  browserHistory,
  entitiesTypes,
  entityTypes,
  localHostnames,
  routesNames,
  userAccountTypes,
} from 'shared/constants';

import type { AccountType } from 'types/generic-context';

import { stringifyArrays } from './strings';

export const getQueryVariables = (qsMode = null) => {
  const searchString = window.location.search.substring(1);
  if (qsMode) {
    return qs.parse(searchString);
  }
  const queryParams = searchString.split('&');
  return queryParams
    .filter((queryParam) => queryParam)
    .reduce<Record<string, string>>((prev, cur) => {
      const [key, value] = cur.split('=').map(decodeURIComponent);

      return {
        ...prev,
        [key]: normalizeValue(value),
      };
    }, {});
};

export const toQueryString = (params: any, withPrefix = false) => {
  const queryString = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
    .join('&');

  return withPrefix && queryString ? `?${queryString}` : queryString;
};

export const setQueryVariables = (update) => {
  const queryVariables = getQueryVariables();

  Object.keys(update).forEach((updateKey) => {
    const updateValue = update[updateKey];
    if (updateValue === undefined) {
      delete queryVariables[updateKey];
    } else {
      queryVariables[updateKey] = updateValue;
    }
  });

  const queryString = toQueryString(queryVariables, true);

  if (queryString !== window.location.search) {
    window.history.pushState(null, null, `${window.location.pathname}${queryString}`);
  }
};

export const getQsVariables = () => {
  const variables = qs.parse(window.location.search, {
    arrayLimit: 10000,
    ignoreQueryPrefix: true,
  });

  return mapValues(parseArrays(variables), normalizeValue);
};

export const setQsVariables = (values) => {
  if (localStorage.disableQs) {
    return;
  }

  const update = stringifyArrays(values);

  const queryString = qs.stringify(
    {
      ...getQsVariables(),
      ...update,
    },
    {
      encodeValuesOnly: true,
      addQueryPrefix: true,
    },
  );

  window.history.pushState(null, null, `${window.location.pathname}${queryString}`);
};

export const pathnameInRoutesNames = (routesNames) => {
  const { pathname } = window.location;
  const pathnameWithSlash = pathname.endsWith('/') ? pathname : `${pathname}/`;
  const routesNamesArray = Array.isArray(routesNames) ? routesNames : [routesNames];
  const prefixedRoutesNames = routesNamesArray.map((route) => `${window.isAgent ? `${agentPrefix}/` : '/'}${route}/`);
  return prefixedRoutesNames.includes(pathnameWithSlash);
};

export const getPathFromObject = ({ pathname, query = {} }) => {
  const queryString = toQueryString(query);
  return `${pathname}${queryString ? `?${queryString}` : ''}`;
};

const getPatchedHistoryMethod = (method) => (pathDescriptor) => {
  const stringifiedPath = typeof pathDescriptor === 'string' ? pathDescriptor : getPathFromObject(pathDescriptor);
  if (['http://', 'https://'].some((protocol) => stringifiedPath.startsWith(protocol))) {
    logError('browserHistory should not be used with absolute urls', {
      url: stringifiedPath,
    });
    // @ts-expect-error
    return (window.location = stringifiedPath);
  }
  browserHistory[method](stringifiedPath);
  return stringifiedPath;
};

browserHistory.replacePatched = getPatchedHistoryMethod('replace');
browserHistory.pushPatched = getPatchedHistoryMethod('push');

export const windowLocationReload = () => window.location.reload();

export const redirectTo = (href) => browserHistory.pushPatched(href);

export const redirectToExternalUrl = (href) => (window.location.href = href);

export const replaceUrl = (href) => browserHistory.replacePatched(href);

export const isPathStartWith = (path) => window.location.pathname.startsWith(path);

export const isPathEqual = (path) => window.location.pathname === path;

export const isLocalHostname = () => localHostnames.includes(window.location.hostname);

export const getCurrentPath = () => window.location.pathname + window.location.search + window.location.hash;

export const openCandidateChat = (candidateId: number) =>
  window.open(`${window.location.origin}${agentPrefix}/chat?name=${candidateId}`, window.hrefTarget);

export function updateQuery(assignment, reset) {
  const query = reset ? {} : this.query || getQueryVariables();
  this.query = {
    ...query,
    ...assignment,
  };
  if (this.lastQueryUpdate && Date.now() - this.lastQueryUpdate < 1000) {
    this.lastQueryUpdateCounter++;
    if (this.lastQueryUpdateCounter > 20) {
      throw new Error('updateQuery overload');
    }
  } else {
    this.lastQueryUpdate = Date.now();
    this.lastQueryUpdateCounter = 1;
  }

  replaceUrl({
    pathname: window.location.pathname,
    query: this.query,
  });
}

export const navigateToJob = (jobId: number) =>
  window.open(`${window.location.origin}${agentPrefix}/jobs?jobId=${jobId}`, window.hrefTarget);

export const getPathInValues = (values: string[], path: string[]) => {
  const pathAsString = path.join('.');
  const pathWithoutId = pathAsString.replace(/Id$/, '');
  return pathAsString.endsWith('Id') && !(pathAsString in values) && pathWithoutId in values
    ? pathWithoutId
    : pathAsString;
};

export const configurableEntitiesPath = (
  entityType: keyof typeof entityTypes,
  entitiesType: ValuesOf<typeof entitiesTypes>,
  entity: any,
  body: Record<string, any> = {},
) => {
  let filter;
  let include;

  if (entityType === 'contact') {
    filter = {
      contact: {
        userId: [entity.userId],
      },
    };
  } else if (entityType === 'company') {
    filter = {
      company: {
        companyId: [entity.companyId],
      },
    };
  } else {
    throw new Error(`configurableEntitiesPath: can't process entityType ${entityType}`);
  }

  if (entitiesType === entitiesTypes.jobs) {
    include = ['salary'];
  } else if (entitiesType === entitiesTypes.contacts) {
    include = [];
  } else {
    throw new Error(`configurableEntitiesPath: can't process entitiesType ${entitiesType}`);
  }

  return [
    `/${entitiesType}/search`,
    {
      method: 'POST',
      body: JSON.stringify({
        filter,
        ...(body.offset === undefined ? { limit: 100 } : {}),
        include,
        orderBy: [],
        ...body,
      }),
    },
  ];
};

export const getLinkPermission = (entitiesType) => {
  if (entitiesType === entitiesTypes.companies) {
    return 'frontend-companiesCrud-view';
  } else if (entitiesType === entitiesTypes.jobs) {
    return 'frontend-jobsCrud-view';
  } else if (entitiesType === entitiesTypes.contacts) {
    return 'frontend-contactsCrud-view';
  } else if (entitiesType === entitiesTypes.candidates) {
    return 'frontend-candidatesCrud-view';
  } else if (entitiesType === entitiesTypes.agents) {
    return 'frontend-agentsCrud-view';
  }
  return '';
};

export const getNextPortalPageUrl = (accountTypes: AccountType[] = [userAccountTypes.candidate]): string => {
  if (!accountTypes) {
    throw new Error('accountTypes not found');
  }

  const portals = getAccessPortals(accountTypes);

  if (isAccessToSelectPortal(portals)) {
    return `${authPrefix}${routesNames.portals}`;
  }

  if (!portals[0]) {
    throw new Error('portal not found');
  }

  return `/${portals[0]}`;
};

export const getTenant = () => window.location.hostname.split('.')[0];
